/**
 * App 頁面名稱對照表
 * key 為 Flutter ScreenTrackingObserver._normalizeRouteName() 產生的值：
 *   - 去除開頭 /，路徑分隔 / → _，保留連字號 -
 * 也包含 GoRouter name: 'xxx' 直接作為 key 的 camelCase 變體。
 * 動態路由（含實際 ID）由 getScreenLabel() 的 prefix matching 處理。
 */
export const screenNameLabels: Record<string, string> = {
  // Main tabs（由 main_shell.dart _tabNames 直接設定）
  info: '護理 Next Step',
  resources: '搜尋環境',
  survey_list: '職涯診斷室',
  career_vision: '場域專業深度剖析',
  community: '護理世界連結',

  // Auth 路由 — path-based (auth_xxx) + GoRoute name camelCase
  auth_login: '登入',
  login: '登入',
  auth_register: '註冊',
  register: '註冊',
  'auth_qr-scanner': '掃描 QR Code',
  qrScanner: '掃描 QR Code',
  'auth_profile-setup': '完善個人資料',
  profileSetup: '完善個人資料',

  // Survey 靜態路由 — path-based + camelCase
  'survey_overall-summary': '綜合總覽',
  overallSummary: '綜合總覽',
  survey_history: '量表歷史',
  surveyHistory: '量表歷史',
  survey_form: '選擇量表',
  surveyForm: '選擇量表',
  surveySummary: '量表摘要',

  // Discussion 靜態路由 — path-based + camelCase
  discussion: '討論區',
  discussionList: '討論區',
  discussion_create: '發起討論',
  createDiscussion: '發起討論',

  // Daily note 路由 — path-based + camelCase
  'daily-note': '每日 note',
  dailyNoteList: '每日 note',
  'daily-note_edit': '編輯 note',
  dailyNoteEdit: '編輯 note',
}

/** 靜態 key 集合，用於 prefix match 時排除 */
const KNOWN_STATIC_KEYS = new Set(Object.keys(screenNameLabels))

export function getScreenLabel(name: string): string {
  if (KNOWN_STATIC_KEYS.has(name)) return screenNameLabels[name]
  // 動態路由：survey_summary_<id> → 量表摘要
  if (name.startsWith('survey_summary_')) return '量表摘要'
  // 動態路由：survey_<id>（非靜態 key）→ 量表結果
  if (name.startsWith('survey_')) return '量表結果'
  // 動態路由：discussion_<id>（非靜態 key）→ 討論詳情
  if (name.startsWith('discussion_')) return '討論詳情'
  // GoRouter camelCase dynamic: discussionDetail
  if (name === 'discussionDetail') return '討論詳情'
  return name
}

/** 各頁面的導覽路徑說明，key 為正規化後的 screenName */
const screenNavLabels: Record<string, string> = {
  info: '底部導覽列 → 第 1 Tab（護理 Next Step）',
  resources: '底部導覽列 → 第 2 Tab（搜尋環境）',
  survey_list: '底部導覽列 → 第 3 Tab（職涯診斷室）',
  career_vision: '底部導覽列 → 第 4 Tab（場域專業深度剖析）',
  community: '底部導覽列 → 第 5 Tab（護理世界連結）',
  auth_login: '未登入時自動跳轉，或主動登出後',
  login: '未登入時自動跳轉，或主動登出後',
  auth_register: '登入頁 → 點擊「建立新帳號」',
  register: '登入頁 → 點擊「建立新帳號」',
  'auth_qr-scanner': '登入頁 → 點擊「QR Code 掃描登入」',
  qrScanner: '登入頁 → 點擊「QR Code 掃描登入」',
  'auth_profile-setup': '首次登入後自動跳轉',
  profileSetup: '首次登入後自動跳轉',
  'survey_overall-summary': '職涯診斷室 → 上方「綜合總覽」按鈕',
  overallSummary: '職涯診斷室 → 上方「綜合總覽」按鈕',
  survey_history: '職涯診斷室 → 「填寫紀錄」頁籤',
  surveyHistory: '職涯診斷室 → 「填寫紀錄」頁籤',
  survey_form: '職涯診斷室 → 點擊「開始填寫」某份量表',
  surveyForm: '職涯診斷室 → 點擊「開始填寫」某份量表',
  'daily-note': '首頁 → 每日 note 入口',
  dailyNoteList: '首頁 → 每日 note 入口',
  'daily-note_edit': '每日 note → 新增或點擊某篇日誌',
  dailyNoteEdit: '每日 note → 新增或點擊某篇日誌',
  discussion: '護理世界連結 → 討論區頁籤',
  discussionList: '護理世界連結 → 討論區頁籤',
  discussion_create: '討論區 → 右下角「+」發起討論',
  createDiscussion: '討論區 → 右下角「+」發起討論',
  discussionDetail: '討論區 → 點擊某個討論主題',
}

export function getScreenNavPath(name: string): string {
  if (screenNavLabels[name]) return screenNavLabels[name]
  if (name.startsWith('survey_summary_') || name === 'surveySummary') return '量表填完後自動跳轉，或歷史列表點選'
  if (name.startsWith('survey_')) return '職涯診斷室相關頁面'
  if (name.startsWith('discussion_')) return '討論區 → 點擊某個討論主題'
  return ''
}

/** 各頁面的 App 內導覽路徑說明（用於後台頁面說明參考表） */
export const SCREEN_PAGE_INFO: Array<{ label: string; keys: string; nav: string }> = [
  { label: '護理 Next Step', keys: 'info', nav: '底部導覽列 → 第 1 Tab' },
  { label: '搜尋環境', keys: 'resources', nav: '底部導覽列 → 第 2 Tab' },
  { label: '職涯診斷室', keys: 'survey_list', nav: '底部導覽列 → 第 3 Tab' },
  { label: '場域專業深度剖析', keys: 'career_vision', nav: '底部導覽列 → 第 4 Tab' },
  { label: '護理世界連結', keys: 'community', nav: '底部導覽列 → 第 5 Tab' },
  { label: '登入', keys: 'auth_login / login', nav: '未登入時自動跳轉，或主動登出後' },
  { label: '註冊', keys: 'auth_register / register', nav: '登入頁 → 點擊「建立新帳號」' },
  { label: '掃描 QR Code', keys: 'auth_qr-scanner / qrScanner', nav: '登入頁 → 點擊「QR Code 掃描登入」' },
  { label: '完善個人資料', keys: 'auth_profile-setup / profileSetup', nav: '首次登入後自動跳轉' },
  { label: '綜合總覽', keys: 'survey_overall-summary / overallSummary', nav: '職涯診斷室 → 上方「綜合總覽」按鈕' },
  { label: '量表歷史', keys: 'survey_history / surveyHistory', nav: '職涯診斷室 → 「填寫紀錄」頁籤' },
  { label: '選擇量表', keys: 'survey_form / surveyForm', nav: '職涯診斷室 → 點擊「開始填寫」某份量表' },
  { label: '量表摘要', keys: 'survey_summary_<id> / surveySummary', nav: '量表填完後自動跳轉，或歷史列表點選' },
  { label: '量表結果', keys: 'survey_<id>（動態）', nav: '職涯診斷室相關頁面（個別量表結果）' },
  { label: '每日 note', keys: 'daily-note / dailyNoteList', nav: '首頁 → 每日 note 入口' },
  { label: '編輯 note', keys: 'daily-note_edit / dailyNoteEdit', nav: '每日 note → 新增或點擊某篇日誌' },
  { label: '討論區', keys: 'discussion / discussionList', nav: '護理世界連結 → 討論區頁籤' },
  { label: '發起討論', keys: 'discussion_create / createDiscussion', nav: '討論區 → 右下角「+」發起討論' },
  { label: '討論詳情', keys: 'discussion_<id> / discussionDetail', nav: '討論區 → 點擊某個討論主題' },
]
